import { useState, useEffect } from 'react';
import { RotateCcw, Lightbulb, TrendingUp, TrendingDown } from 'lucide-react';
import { Input } from '../ui/input';

export interface OperationalMetric {
  id: string;
  name: string;
  description: string;
  previousValue: number;
  currentValue: number;
  impactFactor: number; // How much CO2e change per unit change in this metric
}

interface OperationalMetricsProps {
  onImpactChange: (impacts: { [key: string]: number }) => void;
}

const initialMetrics: OperationalMetric[] = [
  {
    id: 'chep',
    name: 'Total CHEP Pallets',
    description: 'Circular pallets in use',
    previousValue: 25504569,
    currentValue: 89798798,
    impactFactor: -0.00002085 // Negative factor: more pallets = less emissions per pallet
  },
  {
    id: 'supplier',
    name: 'Supplier Integration Flows',
    description: 'Direct supplier connections',
    previousValue: 2207739,
    currentValue: 2324386,
    impactFactor: -0.0007 // Negative factor means increase = less emissions
  },
  {
    id: 'cycle',
    name: 'Cycle Time (days)',
    description: 'Average pallet rotation time',
    previousValue: 33.29,
    currentValue: 33,
    impactFactor: 80 // Lower cycle time = better efficiency
  },
  {
    id: 'transport',
    name: 'Transport Collaboration Mileage Savings',
    description: 'Miles saved through route optimization',
    previousValue: 0,
    currentValue: 701,
    impactFactor: -1.42 // More savings = less emissions
  }
];

export function OperationalMetrics({ onImpactChange }: OperationalMetricsProps) {
  const [metrics, setMetrics] = useState<OperationalMetric[]>(initialMetrics);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingPreviousId, setEditingPreviousId] = useState<string | null>(null);

  const calculateChange = (metric: OperationalMetric): number => {
    if (metric.previousValue === 0) return metric.currentValue > 0 ? Infinity : 0;
    return ((metric.currentValue - metric.previousValue) / metric.previousValue) * 100;
  };

  const calculateImpact = (metric: OperationalMetric): number => {
    const delta = metric.currentValue - metric.previousValue;
    return Math.round(delta * metric.impactFactor);
  };

  useEffect(() => {
    // Always update impacts when metrics change
    const impacts: { [key: string]: number } = {};
    metrics.forEach(metric => {
      impacts[metric.id] = calculateImpact(metric);
    });
    onImpactChange(impacts);
  }, [metrics]);

  const handleValueChange = (id: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setMetrics(prev =>
      prev.map(m => (m.id === id ? { ...m, currentValue: numValue } : m))
    );
  };

  const handlePreviousValueChange = (id: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setMetrics(prev =>
      prev.map(m => (m.id === id ? { ...m, previousValue: numValue } : m))
    );
  };

  const handleReset = () => {
    setMetrics(initialMetrics);
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return new Intl.NumberFormat('en-US').format(Math.round(num));
    }
    return num.toFixed(2);
  };

  const formatChange = (change: number): string => {
    if (change === Infinity) return '+∞';
    if (change === -Infinity) return '-∞';
    return change >= 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
  };

  const getChangeColor = (change: number): string => {
    if (change > 0) return 'text-blue-600 bg-blue-50';
    if (change < 0) return 'text-orange-600 bg-orange-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getImpactColor = (impact: number): string => {
    if (impact > 0) return 'text-red-600 bg-red-50';
    if (impact < 0) return 'text-green-600 bg-green-50';
    return 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-[0_8px_32px_rgb(0,0,0,0.08)] p-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-[#1a1a1a] mb-1">Operational Metrics</h2>
          <p className="text-sm text-gray-600">Edit previous and current values to simulate different scenarios</p>
        </div>
        <button
          onClick={handleReset}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-white border border-gray-300/50 rounded-lg hover:bg-gray-50 hover:shadow-md hover:scale-105 transition-all duration-300"
        >
          <RotateCcw className="w-4 h-4" />
          Reset to Original
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 text-sm text-gray-600">Metric</th>
              <th className="text-right py-3 px-4 text-sm text-gray-600">Previous Value</th>
              <th className="text-right py-3 px-4 text-sm text-gray-600">Current Value</th>
              <th className="text-center py-3 px-4 text-sm text-gray-600">Change</th>
              <th className="text-right py-3 px-4 text-sm text-gray-600">Impact (tCO₂e)</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric, index) => {
              const change = calculateChange(metric);
              const impact = calculateImpact(metric);
              const isEditing = editingId === metric.id;
              const isEditingPrevious = editingPreviousId === metric.id;

              return (
                <tr
                  key={metric.id}
                  className={`border-b border-gray-100 hover:bg-gray-50/50 transition-colors ${index % 2 === 0 ? 'bg-gray-50/30' : ''
                    }`}
                >
                  {/* Metric Name */}
                  <td className="py-4 px-4">
                    <div>
                      <div className="text-sm text-[#1a1a1a]">{metric.name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{metric.description}</div>
                    </div>
                  </td>

                  {/* Previous Value - Editable */}
                  <td className="py-4 px-4">
                    <div className="flex justify-end">
                      <Input
                        type="number"
                        value={metric.previousValue}
                        onChange={(e) => handlePreviousValueChange(metric.id, e.target.value)}
                        onFocus={() => setEditingPreviousId(metric.id)}
                        onBlur={() => setEditingPreviousId(null)}
                        className={`
                          w-32 text-right text-sm bg-gray-50 border-gray-200
                          focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100
                          transition-all duration-200
                          ${isEditingPrevious ? 'shadow-lg scale-105' : ''}
                        `}
                        step="any"
                      />
                    </div>
                  </td>

                  {/* Current Value - Editable */}
                  <td className="py-4 px-4">
                    <div className="flex justify-end">
                      <Input
                        type="number"
                        value={metric.currentValue}
                        onChange={(e) => handleValueChange(metric.id, e.target.value)}
                        onFocus={() => setEditingId(metric.id)}
                        onBlur={() => setEditingId(null)}
                        className={`
                          w-32 text-right text-sm bg-gray-50 border-gray-200
                          focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100
                          transition-all duration-200
                          ${isEditing ? 'shadow-lg scale-105' : ''}
                        `}
                        step="any"
                      />
                    </div>
                  </td>

                  {/* Change % */}
                  <td className="py-4 px-4">
                    <div className="flex justify-center">
                      <div
                        className={`
                          inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs
                          ${getChangeColor(change)}
                          transition-all duration-300
                        `}
                      >
                        {change > 0 && <TrendingUp className="w-3 h-3" />}
                        {change < 0 && <TrendingDown className="w-3 h-3" />}
                        <span>{formatChange(change)}</span>
                      </div>
                    </div>
                  </td>

                  {/* Impact */}
                  <td className="py-4 px-4">
                    <div className="flex justify-end">
                      <div
                        className={`
                          inline-flex items-center px-3 py-1.5 rounded-lg text-sm
                          ${getImpactColor(impact)}
                          transition-all duration-300
                        `}
                      >
                        {impact > 0 ? '+' : ''}{impact}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Tip Section */}
      <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50/80 backdrop-blur-sm border border-blue-200/50 rounded-xl">
        <div className="flex items-start gap-3">
          <Lightbulb className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-700">
            <strong>Tip:</strong> Edit both "Previous Value" and "Current Value" fields to simulate different scenarios and see how changes in operational metrics affect overall environmental impact in real-time.
          </p>
        </div>
      </div>
    </div>
  );
}
